import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormHelperText,
  Card,
  Box,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Select from "@mui/material/Select";
import { useSelector } from "react-redux";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import ShepFormField from "@/components/GenericComponents/ShepFormField";
import { selectEditConnectionAllConnections } from "@/features/connections/integrationSlice";

const connectionTypes = [
  { key: 0, label: "Customer" },
  { key: 1, label: "ERP" },
  { key: 2, label: "Warehouse" },
];

const ConnectionDetails = ({
  connectionDetails,
  setConnectionDetails,
  setHasErrors,
  connectionId,
  showErrors,
}) => {
  const allConnections = useSelector(selectEditConnectionAllConnections);

  // Error handling state, controlled via validateForm, empty when no error is present in the form.
  const [errors, setErrors] = useState({
    connectionName: "",
    displayName: "",
    connectionType: "",
    shortForm: "",
  });

  // Touched state to track if a field has been blurred
  const [touched, setTouched] = useState({
    connectionName: false,
    displayName: false,
    connectionType: false,
    shortForm: false,
  });

  // Anytime the model is updated, i.e any form element changes, check for validation issues
  useEffect(() => {
    const newErrors = validateForm(
      connectionDetails,
      allConnections,
      connectionId
    );
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    setHasErrors(hasErrors);

    if (showErrors) {
      setTouched({
        connectionName: true,
        displayName: true,
        connectionType: true,
        shortForm: true,
      });
    }
  }, [connectionDetails, allConnections, setHasErrors, showErrors]);

  // Update the connection details upon change in the form fields
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setConnectionDetails((prevDetails) => ({
        ...prevDetails,
        [name]: value,
      }));
    },
    [setConnectionDetails]
  );

  // Update the touched state upon blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));
  };

  return (
    <div style={{ width: 550, boxShadow: 0, backgroundColor: "transparent" }}>
      <div>
        <div style={{ width: "300px" }}>Connection Name:</div>
        <FormControl fullWidth>
          <ShepFormField
            variant="outlined"
            name="connectionName"
            value={connectionDetails.connectionName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={touched.connectionName && !!errors.connectionName}
            helperText={touched.connectionName && errors.connectionName}
            style={{
              width: 400,
              marginTop: 8,
              marginBottom: 16,
            }}
            autoComplete="off"
            inputProps={{ maxLength: 150 }}
          />
        </FormControl>
      </div>
      <div>
        <div style={{ width: "300px" }}>Display Name:</div>
        <FormControl fullWidth>
          <TextField
            variant="outlined"
            name="displayName"
            value={connectionDetails.displayName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={touched.displayName && !!errors.displayName}
            helperText={touched.displayName && errors.displayName}
            style={{
              width: 400,
              marginTop: 8,
              marginBottom: 16,
            }}
            autoComplete="off"
            inputProps={{ maxLength: 150 }}
          />
        </FormControl>
      </div>
      <div style={{ marginTop: 8 }}>
        <div style={{ width: "300px" }}>Connection Type:</div>
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <FormControl
            fullWidth
            error={touched.connectionType && !!errors.connectionType}
          >
            <Select
              labelId="connection-type-label"
              id="connection-type"
              name="connectionType"
              variant="outlined"
              value={connectionDetails.connectionType}
              label="Connection Type"
              onChange={handleInputChange}
              onBlur={handleBlur}
              style={{ width: 400 }}
              MenuProps={{
                style: { zIndex: 1500 },
              }}
              aria-label="Connection Type"
            >
              {connectionTypes.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {touched.connectionType && errors.connectionType}
            </FormHelperText>
          </FormControl>
        </div>
      </div>
      <div>
        <div style={{ width: "300px" }}>Short Form:</div>
        <FormControl fullWidth>
          <TextField
            variant="outlined"
            name="shortForm"
            value={connectionDetails.shortForm}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={touched.shortForm && !!errors.shortForm}
            helperText={touched.shortForm && errors.shortForm}
            style={{
              width: 400,
              marginTop: 8,
              marginBottom: 16,
            }}
            autoComplete="off"
          />
        </FormControl>
      </div>
      <div
        style={{
          marginTop: 8,
          display: "flex",
          flexDirection: "row",
          alignItems: "left",
        }}
      >
        <FormControl>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: "20px" }}>Disabled:</div>
            <ShepCheckbox
              edge="start"
              checked={connectionDetails.isDisabled}
              tabIndex={-1}
              disableRipple
              onClick={() =>
                setConnectionDetails((prevDetails) => ({
                  ...prevDetails,
                  isDisabled: !prevDetails.isDisabled,
                }))
              }
            />
          </div>
        </FormControl>
      </div>
    </div>
  );
};

const validateForm = (
  connectionDetails,
  allConnections,
  connectionId = null
) => {
  const newErrors = {};

  //This has no meaningful effect when ConnectionId is not yet set, which is fine
  const otherConnections = allConnections.filter(
    (x) => connectionId !== x.ConnectionId
  );

  if (!connectionDetails.connectionName) {
    newErrors.connectionName = "Connection Name is required.";
  }

  if (
    otherConnections.some(
      (x) => x.ConnectionName === connectionDetails.connectionName
    )
  ) {
    newErrors.connectionName =
      "Connection Name is duplicated with other Connection(s).";
  }

  if (!connectionDetails.displayName) {
    newErrors.displayName = "Display Name is required.";
  }
  if (
    otherConnections.some(
      (x) =>
        x.DisplayName === connectionDetails.displayName &&
        connectionId !== x.ConnectionId
    )
  ) {
    newErrors.displayName =
      "Display Name is duplicated with other Connection(s).";
  }

  if (
    connectionDetails.connectionType === undefined ||
    connectionDetails.connectionType === null
  ) {
    newErrors.connectionType = "Connection Type is required.";
  }

  if (
    !connectionDetails.shortForm ||
    connectionDetails.shortForm.length !== 1
  ) {
    newErrors.shortForm = "Short Form must be one character long.";
  }

  return newErrors;
};

ConnectionDetails.propTypes = {
  connectionDetails: PropTypes.shape({
    connectionName: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    connectionType: PropTypes.string.isRequired,
    shortForm: PropTypes.string.isRequired,
  }).isRequired,
  setConnectionDetails: PropTypes.func.isRequired,
  setHasErrors: PropTypes.func.isRequired,
  connectionId: PropTypes.number,
  showErrors: PropTypes.bool.isRequired,
};

export default ConnectionDetails;
